import { type ReactNode } from 'react';
import { DropdownMenu } from './DropdownMenu';
import { PopoverMenu } from './PopoverMenu';
import { SidebarPanel } from './Sidebar';
import chevronDownWhiteIcon from './icons/chevron-down-white.svg';
import gridDotsWhiteIcon from './icons/grid-dots-white.svg';
import menuWhiteIcon from './icons/menu-white.svg';
import type { AppSelectorInfo, HeaderMenuItem, ProfileMenuItem, User } from './sidebar-types';

interface TopBarProps {
  user: User | null;
  avatarUrl?: string;
  label?: ReactNode;
  bootId?: string;
  appSelectorInfo?: AppSelectorInfo;
  headerMenuItems?: HeaderMenuItem[];
  headerMenuSelectedItem?: HeaderMenuItem;
  isHeaderMenuItemsLoading?: boolean;
  disabledHeaderMenu?: boolean;
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  onLogout: () => void;
  onOpenShortcuts: () => void;
  onOpenBugReport: () => void;
  onOpenFeatureRequest: () => void;
  profileMenuItems?: ProfileMenuItem[];
}

export function TopBar({
  user,
  avatarUrl,
  label = 'COMPLI ONE',
  bootId,
  appSelectorInfo,
  headerMenuItems,
  headerMenuSelectedItem,
  isHeaderMenuItemsLoading,
  disabledHeaderMenu,
  isOpen,
  onOpen,
  onClose,
  onLogout,
  onOpenShortcuts,
  onOpenBugReport,
  onOpenFeatureRequest,
  profileMenuItems,
}: TopBarProps) {
  return (
    <>
      <header className="flex h-14 items-center border-b border-white/10 bg-[#171717] px-4 text-white lg:hidden">
        <div className="min-w-0 flex-1 truncate text-2xl font-serif font-semibold leading-6">
          {label}
        </div>

        {appSelectorInfo && (
          <PopoverMenu
            placement="bottom-start"
            classes={{
              container: '!mt-0 !w-auto z-10',
              panel: 'w-[220px] p-0 shadow-xl !left-1/2 -translate-x-1/2',
            }}
            button={<img src={gridDotsWhiteIcon} alt="app selector" className="p-4 select-none" />}
            items={appSelectorInfo.items}
          />
        )}

        {headerMenuItems && (
          <div className="flex w-[100px] justify-end">
            <DropdownMenu
              items={headerMenuItems}
              className="w-[280px] border shadow-lg"
              selectedValue={headerMenuSelectedItem?.value}
              loading={isHeaderMenuItemsLoading}
              disabled={disabledHeaderMenu}
              containerClassName="!w-auto mt-0"
              button={
                <div className="flex items-center gap-2 p-4">
                  <span className="text-xs uppercase tracking-[0.18em] text-white/75">
                    Register
                  </span>
                  <img className="select-none" src={chevronDownWhiteIcon} alt="Select register" />
                </div>
              }
            />
            <div className="my-3 border-r border-[#FFFFFF1F]" />
          </div>
        )}

        <button
          type="button"
          onClick={onOpen}
          className="ml-auto rounded-md p-2 text-white/70 transition-colors hover:bg-white/5 hover:text-white"
          aria-label="Open menu"
        >
          <img src={menuWhiteIcon} alt="Open menu" className="select-none" />
        </button>
      </header>

      {isOpen && (
        <div className="fixed inset-0 top-14 z-40 bg-[#171717] lg:hidden">
          <SidebarPanel
            user={user}
            avatarUrl={avatarUrl}
            label={label}
            bootId={bootId}
            appSelectorInfo={appSelectorInfo}
            headerMenuItems={headerMenuItems}
            headerMenuSelectedItem={headerMenuSelectedItem}
            isHeaderMenuItemsLoading={isHeaderMenuItemsLoading}
            disabledHeaderMenu={disabledHeaderMenu}
            mobile
            defaultOpened
            profileMenuItems={profileMenuItems}
            onCloseMobile={onClose}
            onLogout={onLogout}
            onOpenShortcuts={onOpenShortcuts}
            onOpenBugReport={onOpenBugReport}
            onOpenFeatureRequest={onOpenFeatureRequest}
          />
        </div>
      )}
    </>
  );
}
